import React, { useState, useEffect } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography, Skeleton, Grid, useTheme } from "@mui/material";
import {
    ResponsiveContainer,
    BarChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Bar,
    Cell,
    PieChart,
    Legend,
    Pie,
} from "recharts";
import { generateYearOptions, MONTHS } from '../../../Reports/ReportHelpers';
import { api } from '../../../../api/api';
import { useAuth } from '../../../../contexts/AuthProvider/AuthProvider';
import { useBillingCompanyContext } from '../../../../providers';
import { fmtCurrency } from '../MainDashboard.constants';

const tooltipStyle = { background: "#1a1a1a", border: "none", borderRadius: 12, color: "#fff", fontSize: 12 };
const COLORS = ["#0d3b66", "#1e5a8e", "#2e7ab8", "#4a9fd8", "#6bb8e8"];

interface EventTypeData {
    event_type: string;
    total_events: number;
    revenue: string;
}

export interface EventPerformanceReportProps {
    frameless?: boolean;
}

export const EventPerformanceReport: React.FC<EventPerformanceReportProps> = ({ frameless = false }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const { user } = useAuth();
    const { comapny } = useBillingCompanyContext();

    // State management
    const currentDate = new Date();
    const [commonYear, setCommonYear] = useState<number>(currentDate.getFullYear());
    const [commonMonth, setCommonMonth] = useState<number | "">(currentDate.getMonth() + 1);
    const [eventChartData, setEventChartData] = useState<{ name: string; v: number }[]>([]);
    const [eventRevenueData, setEventRevenueData] = useState<{ name: string; v: number }[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // Fetch data from API
    const fetchEventTypeReport = async () => {
        try {
            setLoading(true);

            // Calculate date range
            let fromDate: string;
            let toDate: string;

            if (commonMonth === "") {
                // All months - full year
                fromDate = `${commonYear}-01-01`;
                toDate = `${commonYear}-12-31`;
            } else {
                // Specific month
                const year = commonYear;
                const month = commonMonth;
                const lastDay = new Date(year, month, 0).getDate();
                fromDate = `${year}-${String(month).padStart(2, '0')}-01`;
                toDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
            }

            const response = await api.get('/report/get-event-type-report', {
                params: {
                    company_outlet_uuid: comapny.company_outlet_uuid,
                    from_date: fromDate,
                    to_date: toDate,
                },
            });

            if (response.data && response.data.data) {
                const eventTypes: EventTypeData[] = response.data.data;

                // Transform data for event chart
                const chartData = eventTypes
                    .filter((e) => e.total_events > 0)
                    .map((e) => ({
                        name: e.event_type.split(" ")[0], // Shorten name for chart
                        v: e.total_events,
                    }));

                setEventChartData(chartData);

                // Transform data for revenue chart (filter out zero revenue)
                const revenueData = eventTypes
                    .filter((e) => Number(e.revenue) > 0)
                    .map((e) => ({
                        name: e.event_type.split(" ")[0], // Shorten name for chart
                        v: Number(e.revenue),
                    }));
                setEventRevenueData(revenueData);
            }
        } catch (error) {
            console.error("Error fetching event type report:", error);
            setEventChartData([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data on mount and when filters change
    useEffect(() => {
        if (comapny.company_outlet_uuid) {
            fetchEventTypeReport();
        }
    }, [commonYear, commonMonth, comapny.company_outlet_uuid]);

    const selectSx = frameless
        ? {
            borderRadius: "12px",
            fontSize: "0.82rem",
            fontWeight: 600,
            backgroundColor: isDark ? "rgba(30,41,59,0.5)" : "#ffffff",
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)",
            },
        }
        : {};

    // --- DESKTOP VIEW (Original untouched layout & design for big screens) ---
    if (!frameless) {
        return (
            <Box
                sx={{
                    gridColumn: {
                        xs: "span 2",
                        md: "span 4",
                        lg: "span 3",
                    },
                    gridRow: "span 2",
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    p: 2.5,
                    mt: 2,
                }}
            >
                <Box
                    sx={{
                        mb: 2,
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "flex-start", md: "center" },
                        justifyContent: "space-between",
                        gap: { xs: 1.5, md: 0 },
                    }}
                >
                    <Typography variant='h4' fontWeight="bold">
                        Events by Category
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: 2, width: { xs: "100%", md: "auto" } }}>
                        <FormControl size="small" sx={{ flex: { xs: 1, md: "none" }, minWidth: { xs: 0, md: 120 } }}>
                            <InputLabel>Year</InputLabel>
                            <Select
                                value={commonYear}
                                label="Year"
                                onChange={(e) => setCommonYear(Number(e.target.value))}
                                disabled={loading}
                            >
                                {generateYearOptions().map((y) => (
                                    <MenuItem key={y} value={y}>
                                        {y}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ flex: { xs: 1, md: "none" }, minWidth: { xs: 0, md: 120 } }}>
                            <InputLabel>Month</InputLabel>
                            <Select
                                value={commonMonth}
                                label="Month"
                                onChange={(e) => setCommonMonth(e.target.value === "" ? "" : Number(e.target.value))}
                                disabled={loading}
                                renderValue={(value) => {
                                    if (!value) return "All Months";
                                    const month = MONTHS.find((m) => m.value === value);
                                    return month ? month.label : "";
                                }}
                            >
                                <MenuItem value="">All Months</MenuItem>
                                {MONTHS.map((m) => (
                                    <MenuItem key={m.value} value={m.value}>
                                        {m.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ width: "100%", height: 220 }}>
                        <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: 2 }} />
                    </Box>
                ) : eventChartData.length === 0 ? (
                    <Box
                        sx={{
                            width: "100%",
                            height: 220,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            No data available for the selected period
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {/* Bar Chart - Left Side */}
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    backgroundColor: "#f9fafb",
                                    borderRadius: 2,
                                    p: { xs: 1, sm: 2 },
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                                    Events Count
                                </Typography>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={eventChartData} margin={{ top: 5, right: 5, left: -8, bottom: 5 }}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="#f3f0e8"
                                            vertical={false}
                                        />

                                        <XAxis
                                            dataKey="name"
                                            tick={{ fontSize: 11 }}
                                        />

                                        <YAxis tick={{ fontSize: 11 }} width={35} />

                                        <Tooltip
                                            contentStyle={tooltipStyle}
                                            cursor={{ fill: "#fef6e4" }}
                                            formatter={(value: number) => [`${value} events`, 'Count']}
                                        />

                                        <Bar
                                            dataKey="v"
                                            radius={[12, 12, 0, 0]}
                                        >
                                            {eventChartData.map((_, i) => (
                                                <Cell
                                                    key={i}
                                                    fill={COLORS[i % COLORS.length]}
                                                />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Grid>

                        {/* Pie Chart - Right Side */}
                        <Grid item xs={12} md={6}>
                            <Box
                                sx={{
                                    backgroundColor: "#f9fafb",
                                    borderRadius: 2,
                                    p: 2,
                                }}
                            >
                                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                                    Revenue Distribution
                                </Typography>
                                {eventRevenueData.length === 0 ? (
                                    <Box
                                        sx={{
                                            height: 300,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            No revenue data available
                                        </Typography>
                                    </Box>
                                ) : (
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={eventRevenueData}
                                                dataKey="v"
                                                nameKey="name"
                                                innerRadius={60}
                                                outerRadius={90}
                                                paddingAngle={3}
                                                label={(entry) => `${entry.name}: ${fmtCurrency(entry.v)}`}
                                            >
                                                {eventRevenueData.map((_, i) => (
                                                    <Cell
                                                        key={i}
                                                        fill={COLORS[i % COLORS.length]}
                                                    />
                                                ))}
                                            </Pie>

                                            <Tooltip
                                                contentStyle={tooltipStyle}
                                                formatter={(v: number) => [fmtCurrency(v), 'Revenue']}
                                            />

                                            <Legend
                                                wrapperStyle={{ fontSize: 11 }}
                                                iconSize={10}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                )}
            </Box>
        );
    }

    // --- MOBILE VIEW (Frameless, sleek mobile dashboard design) ---
    return (
        <Box
            sx={{
                width: "100%",
                borderRadius: 0,
                backgroundColor: "transparent",
                boxShadow: "none",
                border: "none",
                p: 0,
                mt: 0,
            }}
        >
            <Box
                sx={{
                    mb: 2,
                    display: "flex",
                    flexDirection: { xs: "row", md: "row" },
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        fontSize: "1.15rem",
                        color: theme.palette.text.primary,
                        letterSpacing: "-0.01em",
                    }}
                >
                    Events by Category
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                        <InputLabel id="event-year-select-label" sx={{ fontSize: "0.8rem" }}>
                            Year
                        </InputLabel>
                        <Select
                            labelId="event-year-select-label"
                            value={commonYear}
                            label="Year"
                            onChange={(e) => setCommonYear(Number(e.target.value))}
                            disabled={loading}
                            sx={selectSx}
                        >
                            {generateYearOptions().map((y) => (
                                <MenuItem key={y} value={y} sx={{ fontSize: "0.82rem" }}>
                                    {y}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel id="event-month-select-label" sx={{ fontSize: "0.8rem" }}>
                            Month
                        </InputLabel>
                        <Select
                            labelId="event-month-select-label"
                            value={commonMonth}
                            label="Month"
                            onChange={(e) => setCommonMonth(e.target.value === "" ? "" : Number(e.target.value))}
                            disabled={loading}
                            sx={selectSx}
                            renderValue={(value) => {
                                if (!value) return "All";
                                const month = MONTHS.find((m) => m.value === value);
                                return month ? month.label.substring(0, 3) : "";
                            }}
                        >
                            <MenuItem value="" sx={{ fontSize: "0.82rem" }}>All Months</MenuItem>
                            {MONTHS.map((m) => (
                                <MenuItem key={m.value} value={m.value} sx={{ fontSize: "0.82rem" }}>
                                    {m.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ width: "100%", height: 220 }}>
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={220}
                        sx={{ borderRadius: "18px" }}
                    />
                </Box>
            ) : eventChartData.length === 0 ? (
                <Box
                    sx={{
                        width: "100%",
                        height: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: isDark ? "rgba(30, 41, 59, 0.5)" : "#ffffff",
                        borderRadius: "18px",
                        border: `1px dashed ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
                    }}
                >
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        No data available for the selected period
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {/* Bar Chart - Left Side */}
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                backgroundColor: isDark ? "rgba(30, 41, 59, 0.7)" : "#ffffff",
                                borderRadius: "18px",
                                border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.03)",
                                p: { xs: 2, sm: 2.5 },
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 700,
                                    mb: 1.5,
                                    color: theme.palette.text.primary,
                                    fontSize: "0.88rem",
                                }}
                            >
                                Events Count
                            </Typography>
                            <ResponsiveContainer width="100%" height={240}>
                                <BarChart data={eventChartData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke={isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9"}
                                        vertical={false}
                                    />
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 11, fill: isDark ? "#94a3b8" : "#64748b" }}
                                    />
                                    <YAxis tick={{ fontSize: 11, fill: isDark ? "#94a3b8" : "#64748b" }} width={35} />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: isDark ? "#1e293b" : "#0f172a",
                                            border: "none",
                                            borderRadius: "12px",
                                            color: "#ffffff",
                                            fontSize: "12px",
                                            boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                                        }}
                                        cursor={{ fill: isDark ? "rgba(255,255,255,0.05)" : "rgba(13,59,102,0.04)" }}
                                        formatter={(value: number) => [`${value} events`, 'Count']}
                                    />
                                    <Bar
                                        dataKey="v"
                                        radius={[10, 10, 0, 0]}
                                    >
                                        {eventChartData.map((_, i) => (
                                            <Cell
                                                key={i}
                                                fill={COLORS[i % COLORS.length]}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Grid>

                    {/* Pie Chart - Right Side */}
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                backgroundColor: isDark ? "rgba(30, 41, 59, 0.7)" : "#ffffff",
                                borderRadius: "18px",
                                border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                                boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.03)",
                                p: { xs: 2, sm: 2.5 },
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 700,
                                    mb: 1.5,
                                    color: theme.palette.text.primary,
                                    fontSize: "0.88rem",
                                }}
                            >
                                Revenue Distribution
                            </Typography>
                            {eventRevenueData.length === 0 ? (
                                <Box
                                    sx={{
                                        height: 240,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary">
                                        No revenue data available
                                    </Typography>
                                </Box>
                            ) : (
                                <ResponsiveContainer width="100%" height={240}>
                                    <PieChart>
                                        <Pie
                                            data={eventRevenueData}
                                            dataKey="v"
                                            nameKey="name"
                                            innerRadius={55}
                                            outerRadius={85}
                                            paddingAngle={4}
                                            label={(entry) => `${entry.name}: ${fmtCurrency(entry.v)}`}
                                        >
                                            {eventRevenueData.map((_, i) => (
                                                <Cell
                                                    key={i}
                                                    fill={COLORS[i % COLORS.length]}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: isDark ? "#1e293b" : "#0f172a",
                                                border: "none",
                                                borderRadius: "12px",
                                                color: "#ffffff",
                                                fontSize: "12px",
                                                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                                            }}
                                            formatter={(v: number) => [fmtCurrency(v), 'Revenue']}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: 11, color: isDark ? "#94a3b8" : "#64748b" }}
                                            iconSize={10}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};


